'use client';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useCurrentLocale, useI18n } from '@locales/client';
import { useRouter, useSearchParams } from 'next/navigation';

import { useGameSections } from '@/entities/game/model/hooks';
import { ConditionalWrapperGame } from '@/entities/game/ui/сonditional-wrapper-game';

import { useProviders } from '@/features/select-provider/model/hooks';

import {
  Section,
  SectionBlock,
  SectionBlocks,
} from '@/widgets/game-section/ui/styles';
import { RenderCategories } from '@/widgets/game-sections/ui/render-categories';

import { withBreakpointsSlice } from '@/shared/providers/utils/with-breakpoints-slice';
import { SkeletonLoader } from '@/shared/ui/skeleton';
import {
  BodyTextRegularPrimary,
  BoldHeadingPrimary,
  BoldHeadingQuinary,
  BoldHeadingSecondary,
} from '@/shared/ui/typography';
import { useUser } from '@/shared/providers/user-provider/use-user';
import { Box } from '@/shared/ui/box';
import { Button } from '@/shared/ui/form/button';
import { BodyTextMediumB } from '@/shared/ui/typography2';
import { processText } from '@/shared/libs/process-text';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { Link } from '@/shared/ui/link';

import {
  NotFound,
  Provider,
  ProviderGamesCount,
  Providers,
  ProvidersWrapper,
  Wrapper,
} from './styles';
import JackpotLobbyWidget from '@/widgets/jackpot-component/JackpotLobbyWidget';
import useIsMobile from '@/shared/hooks/useIsMobile';
import { EmptySearchResults } from './empty-search-results';
import { RecordWins } from '@/widgets/record-wins/RecordWins';

const GameSections = ({
  mobileLandscape,
  openNotAlowedModal,
  isGames,
  isMobileMenu,
}) => {
  const { showCategories, games = [], isSectionsLoading } = useGameSections();
  const { user } = useUser();

  const [currentGame, setCurrentGame] = useState(null);
  const itemsPerPage = 80;
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const searchParams = useSearchParams();
  const { providers, isLoading: isProviderLoading } = useProviders();
  const [visibleProviderCount, setVisibleProviderCount] = useState(10);
  const t = useI18n();
  const chunkedProviders = Array.from({ length: 2 }, (_, rowIndex) =>
    providers?.filter((_, i) => i % 2 === rowIndex)
  );
  const locale = useCurrentLocale();
  const router = useRouter();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isSectionsLoading) return;
    if (showCategories) return;
    setLoading(true);
    const newItems = games.slice(0, page * itemsPerPage);
    setItems(newItems);
    setLoading(false);
  }, [page, isSectionsLoading, searchParams, user.wishlist]);

  useEffect(() => {
    const handleScroll = () => {
      if (loading) return;
      if (showCategories) return;

      if (
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 1
      ) {
        setPage((prevPage) => prevPage + 1);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loading, showCategories]);

  const getGamePath = (game, demo = false) => {
    const { providerId, title } = game;
    return `/${locale}/games/${processText(providerId)}/${processText(title)}/${demo ? 'demo' : 'play'}`;
  };
  const getGameInfoPath = (game, demo = false) => {
    const { providerId, title } = game;
    return `/${locale}/games/${processText(providerId)}/${processText(title)}`;
  };

  const onGameClick = (game) => {
    if (!isMobile && user.isAuth) {
      router.push(getGamePath(game));
    } else {
      router.push(getGameInfoPath(game));
    }
  };

  const handleToggle = () => {
    if (isExpanded) {
      setVisibleProviderCount(10);
    } else {
      setVisibleProviderCount((prev) => providers.length);
    }
  };

  const isExpanded =
    !isProviderLoading &&
    visibleProviderCount >=
      [...chunkedProviders[0], ...chunkedProviders[1]].length;

  const styleForImage = {
    backgroundImage: `url(/images/background/${mobileLandscape ? 'mob-loader-card' : 'desk-loader-card'}.svg)`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
  };

  if (isMobileMenu) {
    if (!searchParams.get('search')) {
      return;
    }

    if (games.length === 0) {
      return (
        <Wrapper>
          <NotFound>
            <BodyTextRegularPrimary color="#AFAFAF">
              {t('common.not_found')}
            </BodyTextRegularPrimary>
          </NotFound>
        </Wrapper>
      );
    }

    return (
      <>
        <Box $margin="24px 0 14px">
          <BoldHeadingPrimary>
            {t('menu_banner.search_results')}
          </BoldHeadingPrimary>
        </Box>

        <Section $hasScroll={false}>
          {!currentGame?.id &&
            !!items?.length &&
            items.map((game) => (
              <ConditionalWrapperGame
                key={game.id}
                game={game}
                handleClickGame={onGameClick}
                hasScroll={false}
              />
            ))}
        </Section>
      </>
    );
  }

  if (isSectionsLoading) {
    return (
      <Wrapper>
        <SectionBlocks>
          <SectionBlock>
            <BoldHeadingQuinary>
              <SkeletonLoader width={148} height={36} />
            </BoldHeadingQuinary>
          </SectionBlock>
          <SectionBlock>
            <SkeletonLoader width={148} height={36} />
          </SectionBlock>
        </SectionBlocks>
        <Section $hasScroll={true}>
          {[...Array(6)].map((_, index) => (
            <SkeletonLoader
              key={uuidv4()}
              style={styleForImage}
              width={mobileLandscape ? 127 : 178}
              height={mobileLandscape ? 170 : 238}
            />
          ))}
        </Section>
        <SectionBlocks>
          <SectionBlock>
            <BoldHeadingQuinary>
              <SkeletonLoader width={148} height={36} />
            </BoldHeadingQuinary>
          </SectionBlock>
          <SectionBlock>
            <SkeletonLoader width={148} height={36} />
          </SectionBlock>
        </SectionBlocks>
        <Section $hasScroll={true}>
          {[...Array(6)].map((_, index) => (
            <SkeletonLoader
              key={uuidv4()}
              style={styleForImage}
              width={mobileLandscape ? 127 : 178}
              height={mobileLandscape ? 170 : 238}
            />
          ))}
        </Section>
        <SectionBlocks>
          <SectionBlock>
            <BoldHeadingQuinary>
              <SkeletonLoader width={148} height={36} />
            </BoldHeadingQuinary>
          </SectionBlock>
          <SectionBlock>
            <SkeletonLoader width={148} height={36} />
          </SectionBlock>
        </SectionBlocks>
        <Section $hasScroll={true}>
          {[...Array(6)].map((_, index) => (
            <SkeletonLoader
              key={uuidv4()}
              style={styleForImage}
              width={mobileLandscape ? 127 : 178}
              height={mobileLandscape ? 170 : 238}
            />
          ))}
        </Section>
      </Wrapper>
    );
  }

  if (showCategories) {
    return (
      <Wrapper $hasScroll={true}>
        <RenderCategories
          isGames={isGames}
          mobileLandscape={mobileLandscape}
          openNotAlowedModal={openNotAlowedModal}
          setCurrentGame={onGameClick}
        />

        <RecordWins />
        <JackpotLobbyWidget
          onClick={() => {
            router.push('games/vamos');
          }}
        />

        <SectionBlocks>
          <BoldHeadingSecondary
          // style={{
          //   marginLeft: 0,
          //   marginTop: 32,
          //   marginBottom: 24,
          //   textAlign: 'left',
          // }}
          >
            {t('bonus.card.modal_providers')}
          </BoldHeadingSecondary>
        </SectionBlocks>
        {mobileLandscape && !isProviderLoading && (
          <ProvidersWrapper>
            <Providers>
              {chunkedProviders[0].map((item) => (
                <Link key={item.id} href={`games/${item.id}`} legacyBehavior>
                  <Provider $imageUrl={item.imageUrl}>
                    <ProviderGamesCount>{item.gamesCount}</ProviderGamesCount>
                  </Provider>
                </Link>
              ))}
            </Providers>
            <Providers>
              {chunkedProviders[1].map((item) => (
                <Link key={item.id} href={`games/${item.id}`} legacyBehavior>
                  <Provider $imageUrl={item.imageUrl}>
                    <ProviderGamesCount>{item.gamesCount}</ProviderGamesCount>
                  </Provider>
                </Link>
              ))}
            </Providers>
          </ProvidersWrapper>
        )}

        {!mobileLandscape && !isProviderLoading && (
          <ProvidersWrapper>
            <Providers>
              {providers.slice(0, visibleProviderCount).map((item) => (
                <Link key={item.id} href={`games/${item.id}`} legacyBehavior>
                  <Provider $imageUrl={item.imageUrl}>
                    <ProviderGamesCount>{item.gamesCount}</ProviderGamesCount>
                  </Provider>
                </Link>
              ))}
            </Providers>

            <Button
              onClick={handleToggle}
              style={{ marginTop: '20px', marginBottom: '60px' }}
              variant="gray"
            >
              <BodyTextMediumB>
                {isExpanded
                  ? t('common.provider.hide')
                  : t('common.provider.show_more')}
              </BodyTextMediumB>
            </Button>
          </ProvidersWrapper>
        )}
      </Wrapper>
    );
  }

  if (!isSectionsLoading && !showCategories && games.length === 0) {
    return (
      <Wrapper $hasScroll={true}>
        <NotFound>
          <BodyTextRegularPrimary color="#AFAFAF">
            {t('common.not_found')}
          </BodyTextRegularPrimary>
        </NotFound>

        <RenderCategories
          isGames={isGames}
          mobileLandscape={mobileLandscape}
          openNotAlowedModal={openNotAlowedModal}
          setCurrentGame={onGameClick}
          isNotFound
        />
      </Wrapper>
    );
  }

  if (!showCategories) {
    return (
      <Wrapper>
        <Section $hasScroll={false}>
          {items?.map((game) => (
            <ConditionalWrapperGame
              key={game.id}
              game={game}
              handleClickGame={onGameClick}
              hasScroll={false}
            />
          ))}
        </Section>
      </Wrapper>
    );
  }

  return null;
};

const GameSectionsWithBreakpointsSlice = withBreakpointsSlice(
  GameSections,
  ({ mobileLandscape }) => ({ mobileLandscape })
);
export { GameSectionsWithBreakpointsSlice as GameSections };
